// Validation helpers reused from Merchant patterns
import InputsValidations from '../../../modules/InputsValidations.js';

export const validateEmail = (email) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const validatePhone = (phone) => {
	const phoneRegex = /^[\+]?[^A-Za-z]{7,16}$/;
	return phone ? phoneRegex.test(String(phone).replace(/[\s\-\(\)]/g, '')) : true;
};

export const validateZipCode = (zip) => {
	const zipRegex = /^\d{5}$/;
	return zipRegex.test(zip);
};

export const isValidDateMMDDYYYY = (value) => {
	if (!value) return false;
	const re = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
	return re.test(value);
};

export const validateBusinessInfo = (form) => {
	const errors = {};
	
	// Use merchant validations for each field
	Object.keys(InputsValidations.businessInformationValidations).forEach(fieldName => {
		const validation = InputsValidations.businessInformationValidations[fieldName];
		const result = validation(form[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
		}
	});
	
	return errors;
};

export const validateOwnerInfo = (o) => {
	const errors = {};
	
	// Use merchant validations for each field
	Object.keys(InputsValidations.ownerInformationValidations).forEach(fieldName => {
		const validation = InputsValidations.ownerInformationValidations[fieldName];
		const result = validation(o[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
		}
	});
	
	return errors;
};

export const validateOwnerAddress = (address) => {
	const errors = {};
	
	// Use merchant validations for each field
	Object.keys(InputsValidations.ownerAddressValidations).forEach(fieldName => {
		const validation = InputsValidations.ownerAddressValidations[fieldName];
		const result = validation(address[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
		}
	});
	
	return errors;
};

export const validateOwnerContact = (contact) => {
	const errors = {};
	
	// Use merchant validations for each field
	Object.keys(InputsValidations.ownerContactValidations).forEach(fieldName => {
		const validation = InputsValidations.ownerContactValidations[fieldName];
		const result = validation(contact[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
		}
	});
	
	return errors;
};

// Bank information validations
export const validateBankInfo = (bankInfo) => {
	const errors = {};

	if (!bankInfo) return errors;

	// New structure support: { banks: Bank[], draft: BankDraft }
	if (Array.isArray(bankInfo.banks) || bankInfo.draft) {
		const draft = bankInfo.draft || {};
		const hasStarted = (
			(draft.type && String(draft.type).trim() !== '') ||
			(draft.bankName && String(draft.bankName).trim() !== '') ||
			(draft.accountName && String(draft.accountName).trim() !== '') ||
			(draft.accountNumber && String(draft.accountNumber).trim() !== '') ||
			(draft.routingNumber && String(draft.routingNumber).trim() !== '') ||
			(draft.bankAddress && String(draft.bankAddress).trim() !== '') ||
			(draft.bankCountry && String(draft.bankCountry).trim() !== '')
		);

		if (!hasStarted) {
			return errors;
		}

		// Validate required fields for in-progress draft
		const draftErrors = {};
		if (!draft.type || String(draft.type).trim() === '') { draftErrors.type = 'Type is required'; }
		if (!draft.bankName || draft.bankName.trim() === '') { draftErrors.bankName = 'Bank Name is required'; }
		if (!draft.accountName || draft.accountName.trim() === '') { draftErrors.accountName = 'Account Name is required'; }
		if (!draft.accountNumber || draft.accountNumber.trim() === '') {
			draftErrors.accountNumber = 'Account Number is required';
		} else if (!/^\d+$/.test(draft.accountNumber)) {
			draftErrors.accountNumber = 'Account Number must contain only digits';
		} else if (draft.accountNumber.length > 19) {
			draftErrors.accountNumber = 'Account Number cannot exceed 19 digits';
		}
		if (!draft.routingNumber || draft.routingNumber.trim() === '') {
			draftErrors.routingNumber = 'Routing Number is required';
		} else if (!/^\d{9}$/.test(draft.routingNumber)) {
			draftErrors.routingNumber = 'Routing Number must be exactly 9 digits';
		}
		if (!draft.bankAddress || draft.bankAddress.trim() === '') { draftErrors.bankAddress = 'Bank Address is required'; }
		if (draft.bankAddress && draft.bankAddress.length > 500) { draftErrors.bankAddress = 'Bank Address cannot exceed 500 characters'; }
		if (!draft.bankCountry || String(draft.bankCountry).trim() === '') { draftErrors.bankCountry = 'Bank Country is required'; }
		if (draft.bankCountry && draft.bankCountry.length > 255) { draftErrors.bankCountry = 'Bank Country cannot exceed 255 characters'; }
		if (draft.bankCity && draft.bankCity.length > 255) { draftErrors.bankCity = 'Bank City cannot exceed 255 characters'; }
		if (draft.bankState && draft.bankState.length > 255) { draftErrors.bankState = 'Bank State cannot exceed 255 characters'; }

		if (Object.keys(draftErrors).length > 0) {
			return { draft: draftErrors };
		}

		return errors;
	}

	// Legacy structure support: flat object or { wire, ach }
	const hasStartedFilling = bankInfo.bankName || bankInfo.accountName || bankInfo.accountNumber || 
		bankInfo.routingNumber || bankInfo.bankAddress || bankInfo.bankCountry;
	if (!hasStartedFilling) {
		return errors;
	}
	if (!bankInfo.bankName || bankInfo.bankName.trim() === '') { errors.bankName = 'Bank Name is required'; }
	if (!bankInfo.accountName || bankInfo.accountName.trim() === '') { errors.accountName = 'Account Name is required'; }
	if (!bankInfo.accountNumber || bankInfo.accountNumber.trim() === '') {
		errors.accountNumber = 'Account Number is required';
	} else if (!/^\d+$/.test(bankInfo.accountNumber)) {
		errors.accountNumber = 'Account Number must contain only digits';
	} else if (bankInfo.accountNumber.length > 19) {
		errors.accountNumber = 'Account Number cannot exceed 19 digits';
	}
	if (!bankInfo.routingNumber || bankInfo.routingNumber.trim() === '') {
		errors.routingNumber = 'Routing Number is required';
	} else if (!/^\d{9}$/.test(bankInfo.routingNumber)) {
		errors.routingNumber = 'Routing Number must be exactly 9 digits';
	}
	if (!bankInfo.bankAddress || bankInfo.bankAddress.trim() === '') { errors.bankAddress = 'Bank Address is required'; }
	if (bankInfo.bankAddress && bankInfo.bankAddress.length > 500) { errors.bankAddress = 'Bank Address cannot exceed 500 characters'; }
	if (!bankInfo.bankCountry || bankInfo.bankCountry.trim() === '') { errors.bankCountry = 'Bank Country is required'; }
	if (bankInfo.bankCountry && bankInfo.bankCountry.length > 255) { errors.bankCountry = 'Bank Country cannot exceed 255 characters'; }
	if (bankInfo.bankCity && bankInfo.bankCity.length > 255) { errors.bankCity = 'Bank City cannot exceed 255 characters'; }
	if (bankInfo.bankState && bankInfo.bankState.length > 255) { errors.bankState = 'Bank State cannot exceed 255 characters'; }

	return errors;
};

const isSectionEmpty = (s) => {
	if (!s) return true;
	const fields = ['bankName', 'accountName', 'accountNumber', 'routingNumber', 'bankAddress', 'bankCountry', 'bankState', 'bankCity'];
	return fields.every((k) => !String(s[k] || '').trim());
};

export const isBankInfoEmpty = (b) => {
	if (!b) return true;
	// New structure: banks array + draft
	if (Array.isArray(b.banks) || b.draft) {
		const draftEmpty = isSectionEmpty(b.draft || {});
		const banksEmpty = !Array.isArray(b.banks) || b.banks.length === 0;
		return draftEmpty && banksEmpty;
	}
	// Legacy: flat or two-section
	if (b.wire || b.ach) {
		return isSectionEmpty(b.wire) && isSectionEmpty(b.ach);
	}
	return isSectionEmpty(b);
};

export const validateRequestedAmount = (value) => {
	if (value === null || value === undefined || String(value).trim() === '') return 'Requested Amount is required';
	const num = Number(value);
	if (Number.isNaN(num)) return 'Requested Amount must be a number';
	if (num <= 0) return 'Requested Amount must be greater than 0';
	return null;
};

export const validateTotalSubscribers = (value) => {
	if (value === null || value === undefined || String(value).trim() === '') return 'Total Subscribers is required';
	const num = Number(value);
	if (Number.isNaN(num)) return 'Total Subscribers must be a number';
	if (num <= 0) return 'Total Subscribers must be greater than 0';
	if (!Number.isInteger(num)) return 'Total Subscribers must be a whole number';
	return null;
};

export const validateTotalMonthlyRevenue = (value) => {
	if (value === null || value === undefined || String(value).trim() === '') return 'Total Monthly Revenue is required';
	const num = Number(value);
	if (Number.isNaN(num)) return 'Total Monthly Revenue must be a number';
	if (num <= 0) return 'Total Monthly Revenue must be greater than 0';
	return null;
};

export const validateOwnerAge = (birthDate) => {
	if (!birthDate) return 'Date of birth is required';
	
	try {
		const birth = new Date(birthDate);
		const today = new Date();
		
		// Calcular edad en años
		let age = today.getFullYear() - birth.getFullYear();
		const monthDiff = today.getMonth() - birth.getMonth();
		
		// Ajustar si aún no ha cumplido años este año
		if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
			age--;
		}
		
		if (age < 18) {
			return 'Owner must be at least 18 years old';
		}
		
		return null;
	} catch (e) {
		return 'Invalid date format';
	}
};




